import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import CircularProgress from '@material-ui/core/CircularProgress';
import dayjs from 'dayjs';
import { Button } from '../common/Buttons';
import { TextField, DatePicker } from '../common/Inputs';
import { ModalDialog } from '../common/Dialogs';
import Typography from '../common/Typography';
import { useTrans } from '../../services/i18n';
import { config } from '../../services/api';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

const generateTempId = () => `manual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const useStyles = makeStyles((theme) => ({
  form: {
    padding: theme.spacing(2),
  },
  actions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
  uploadSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    marginBottom: theme.spacing(2),
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(3, 0),
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: theme.palette.divider,
  },
  dividerText: {
    padding: theme.spacing(0, 2),
  },
  uploadButton: {
    marginTop: theme.spacing(1),
  },
}));

const OPPORTUNITY_TYPES = [
  { value: 'contract', label: 'Federal Contract Opportunity' },
  { value: 'grant', label: 'Federal Grant Opportunity' },
  { value: 'nsn', label: 'Federal NSN Opportunity' },
  { value: 'award', label: 'Contract Award' },
  { value: 'stateLocal', label: 'State & Local Contract Opportunity' },
  { value: 'stateLocalForecast', label: 'State & Local Forecast' },
];

const AddOpportunityManuallyForm = ({ open, onClose, onSubmit, stageId, pipelineId }) => {
  const classes = useStyles();
  const { trans } = useTrans();

  const [formData, setFormData] = useState({
    pursuitName: '',
    description: '',
    opportunityType: 'contract',
    dueDate: '',
    agencyName: '',
    setAside: '',
    estimatedAwardDate: '',
    winProbability: '',
    estimatedValue: '',
  });

  const [errors, setErrors] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsing, setParsing] = useState(false);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleDateChange = (field) => (newValue) => {
    setFormData({
      ...formData,
      [field]: newValue,
    });
    // Clear error when user selects a date
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedFile(file);

    // Attempt text extraction for PDF/DOCX to pre-populate form fields
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isDocx =
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.name.toLowerCase().endsWith('.docx');

    if (isPdf || isDocx) {
      setParsing(true);
      try {
        const body = new FormData();
        body.append('file', file);

        const response = await fetch(`${config.apiHost}/parse-sow-pdf`, {
          method: 'POST',
          body,
        });
        const result = await response.json();

        if (result.success && (result.extracted.title || result.extracted.description || result.extracted.agencyName)) {
          setFormData((prev) => ({
            ...prev,
            pursuitName: result.extracted.title || prev.pursuitName,
            description: result.extracted.description || prev.description,
            agencyName: result.extracted.agencyName || prev.agencyName,
          }));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('File parsing failed:', error);
      } finally {
        setParsing(false);
      }
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.pursuitName.trim()) {
      newErrors.pursuitName = trans('field-required');
    }
    if (!formData.opportunityType) {
      newErrors.opportunityType = trans('field-required');
    }
    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Create a fake opportunity structure
    const opportunity = {
      id: generateTempId(), // Generate a unique ID for this manual opportunity
      title: formData.pursuitName,
      description: formData.description,
      agencyId: formData.agencyName || null,
      setAside: formData.setAside || null,
      dueDate: formData.dueDate ? dayjs(formData.dueDate).format('YYYY-MM-DD') : null,
      estimatedAwardDate: formData.estimatedAwardDate ? dayjs(formData.estimatedAwardDate).format('YYYY-MM-DD') : null,
      winProbability: formData.winProbability ? parseFloat(formData.winProbability) : null,
      estimatedValue: formData.estimatedValue ? parseFloat(formData.estimatedValue) : null,
      naics: [],
      psc: [],
      pointOfContacts: [],
    };

    onSubmit({
      pipelineId,
      stageId,
      opportunity,
      opportunityType: formData.opportunityType,
      isManual: true, // Flag to indicate this is a manual entry
      uploadedFile, // Raw file for S3 upload after pursuit creation
    });

    // Reset form
    setFormData({
      pursuitName: '',
      description: '',
      opportunityType: 'contract',
      dueDate: '',
      agencyName: '',
      setAside: '',
      estimatedAwardDate: '',
      winProbability: '',
      estimatedValue: '',
    });
    setErrors({});
    setUploadedFile(null);
    onClose();
  };

  const handleCancel = () => {
    setFormData({
      pursuitName: '',
      description: '',
      opportunityType: 'contract',
      dueDate: '',
      agencyName: '',
      setAside: '',
      estimatedAwardDate: '',
      winProbability: '',
      estimatedValue: '',
    });
    setErrors({});
    setUploadedFile(null);
    onClose();
  };

  return (
    <ModalDialog open={open} onClose={handleCancel} title={trans('add-opportunity-manually')} maxWidth="md" fullWidth>
      <div className={classes.form}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body2" color="hint" style={{ marginBottom: 16 }}>
              {trans('add-opportunity-manually-description')}
            </Typography>
          </Grid>

          {/* Upload File Section */}
          <Grid item xs={12}>
            <Box className={classes.uploadSection}>
              {parsing ? (
                <>
                  <CircularProgress size={48} style={{ marginBottom: 8 }} />
                  <Typography variant="h6" weight="500" style={{ marginBottom: 4 }}>
                    {trans('analyzing-document')}
                  </Typography>
                  <Typography variant="body2" color="hint">
                    {trans('extracting-information-from-document')}
                  </Typography>
                </>
              ) : (
                <>
                  <CloudUploadIcon style={{ fontSize: 48, color: '#9CA3AF', marginBottom: 8 }} />
                  <Typography variant="h6" weight="500" style={{ marginBottom: 4 }}>
                    {trans('upload-opportunity-file')}
                  </Typography>
                  <Typography variant="body2" color="hint" style={{ marginBottom: 16, textAlign: 'center' }}>
                    {trans('upload-opportunity-file-description')}
                  </Typography>
                  <input
                    accept=".pdf,.doc,.docx,.txt"
                    style={{ display: 'none' }}
                    id="upload-file-button"
                    type="file"
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="upload-file-button">
                    <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />}>
                      {trans('choose-file')}
                    </Button>
                  </label>
                  {uploadedFile && (
                    <Box mt={2} display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">{uploadedFile.name}</Typography>
                      <Button size="small" onClick={handleRemoveFile}>
                        {trans('remove')}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Grid>

          {/* Divider */}
          <Grid item xs={12}>
            <Box className={classes.divider}>
              <div className={classes.dividerLine} />
              <Typography variant="body2" color="hint" className={classes.dividerText}>
                {trans('or-enter-manually')}
              </Typography>
              <div className={classes.dividerLine} />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={trans('opportunity-name')}
              value={formData.pursuitName}
              onChange={handleChange('pursuitName')}
              error={!!errors.pursuitName}
              helperText={errors.pursuitName}
              placeholder={trans('enter-opportunity-name')}
              required
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label={trans('opportunity-type')}
              value={formData.opportunityType}
              onChange={handleChange('opportunityType')}
              error={!!errors.opportunityType}
              helperText={errors.opportunityType}
              required
              fullWidth
            >
              {OPPORTUNITY_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={trans('description')}
              value={formData.description}
              onChange={handleChange('description')}
              placeholder={trans('enter-description')}
              multiline
              rows={4}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label={trans('agency-name')}
              value={formData.agencyName}
              onChange={handleChange('agencyName')}
              placeholder={trans('enter-agency-name')}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label={trans('set-aside')}
              value={formData.setAside}
              onChange={handleChange('setAside')}
              placeholder={trans('enter-set-aside')}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              InputProps={{
                startAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              label={trans('Win Probability')}
              placeholder={trans('required')}
              maxLength={3}
              align="center"
              value={formData.winProbability}
              onChange={handleChange('winProbability')}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label={trans('Estimated Value')}
              placeholder={trans('required')}
              InputProps={{
                startAdornment: <InputAdornment position="end">$</InputAdornment>,
              }}
              inputProps={{ inputMode: 'numeric' }}
              align="center"
              value={formData.estimatedValue}
              onChange={handleChange('estimatedValue')}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label={trans('Due Date')}
              placeholder={trans('required')}
              maxLength={5}
              align="center"
              disablePast
              disableToolbar
              orientation="landscape"
              value={formData.dueDate || null}
              onChangeValue={handleDateChange('dueDate')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label={trans('Est. Award Date')}
              placeholder={trans('required')}
              maxLength={5}
              align="center"
              value={formData.estimatedAwardDate || null}
              onChangeValue={handleDateChange('estimatedAwardDate')}
            />
          </Grid>
        </Grid>

        <div className={classes.actions}>
          <Button variant="outlined" onClick={handleCancel}>
            {trans('cancel')}
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={parsing}>
            {trans('add-to-pipeline')}
          </Button>
        </div>
      </div>
    </ModalDialog>
  );
};

export default AddOpportunityManuallyForm;
